import { useState, useEffect } from 'react';
import { Search, UserPlus, Shield, Edit, Trash2, Check, X, Key } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import AuthGuard from '@/components/auth/AuthGuard';
import { supabase, SUPABASE_URL } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

interface ExtendedProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  role: string | null;
  created_at: string;
  updated_at: string;
  status: string | null;
  last_active: string | null;
  email: string | null;
}

type User = {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'editor' | 'user';
  avatar: string;
  lastActive: string;
  status: 'active' | 'inactive';
};

const UsersPage = () => {
  const { isAdmin, refreshProfile, user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<null | User>(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    role: 'editor',
    password: '',
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      const { data: profilesData, error } = await supabase
        .from('profiles')
        .select('*')
        .order('last_active', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      console.log("Fetched profiles data:", profilesData);
      
      const profiles = (profilesData || []) as ExtendedProfile[];
      
      // Fetch roles from user_roles table
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');
      
      if (rolesError) {
        console.error("Error fetching roles:", rolesError);
      }
      
      // Create a map of user_id to role
      const rolesMap = new Map<string, string>();
      (rolesData || []).forEach((roleEntry) => {
        rolesMap.set(roleEntry.user_id, roleEntry.role);
      });
      
      const formattedUsers = profiles.map((profile: ExtendedProfile) => ({
        id: profile.id,
        name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unnamed User',
        email: profile.email || 'unknown@example.com',
        role: (rolesMap.get(profile.id) || 'user') as 'admin' | 'editor' | 'user',
        avatar: profile.avatar_url || '',
        lastActive: profile.last_active || new Date().toISOString(),
        status: (profile.status || 'active') as 'active' | 'inactive',
      }));
      
      console.log("Formatted users:", formattedUsers);
      setUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!newUserData.name.trim() || !newUserData.email.trim() || !newUserData.password) {
        toast.error("Please fill in all required fields");
        return;
      }
      
      const nameParts = newUserData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUserData.email.trim(),
        password: newUserData.password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            role: newUserData.role
          }
        }
      });
      
      if (authError) {
        throw authError;
      }
      
      if (!authData.user) {
        throw new Error("Failed to create user");
      }

      // Update profile with name and email
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          first_name: firstName,
          last_name: lastName,
          email: newUserData.email.trim()
        })
        .eq('id', authData.user.id);
        
      if (updateError) {
        throw updateError;
      }

      // Insert role into user_roles table
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert([{
          user_id: authData.user.id,
          role: newUserData.role as any
        }]);
      
      if (roleError) {
        console.error("Error inserting role:", roleError);
        throw roleError;
      }
      
      toast.success("User created successfully");
      setNewUserData({ name: '', email: '', role: 'editor', password: '' });
      setNewUserDialogOpen(false);
      
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error: any) {
      console.error("Error creating user:", error);
      toast.error(`Failed to create user: ${error.message || "Unknown error"}`);
    }
  };

  const handleEditUser = async () => {
    try {
      if (!currentUser) return;
      
      setUpdateLoading(true);
      console.log("Starting user update for:", currentUser.id);
      console.log("New role:", currentUser.role);
      console.log("New status:", currentUser.status);
      
      const nameParts = currentUser.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Update profile (name and status)
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          status: currentUser.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);
        
      if (error) {
        console.error("Update error:", error);
        toast.error(`Failed to update user: ${error.message}`);
        throw error;
      }

      // Update role in user_roles table (delete old and insert new)
      const { error: deleteRoleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', currentUser.id);
      
      if (deleteRoleError) {
        console.error("Error deleting old role:", deleteRoleError);
        throw deleteRoleError;
      }

      const { error: insertRoleError } = await supabase
        .from('user_roles')
        .insert([{
          user_id: currentUser.id,
          role: currentUser.role as any
        }]);
      
      if (insertRoleError) {
        console.error("Error inserting new role:", insertRoleError);
        throw insertRoleError;
      }
      
      console.log("User updated successfully");
      toast.success("User updated successfully");
      setEditUserDialogOpen(false);
      setCurrentUser(null);
      
      await fetchUsers();
      
      await refreshProfile();
      
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast.error(`Failed to update user: ${error.message || "Unknown error"}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    try {
      // Soft delete: deactivate user and remove all roles
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ status: 'inactive' })
        .eq('id', id);
          
      if (profileError) {
        throw profileError;
      }

      // Remove all roles from user_roles table for security
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', id);
      
      if (roleError) {
        console.error("Error removing roles:", roleError);
        // Don't throw - profile is already deactivated, which is the main goal
      }
      
      toast.success("User deactivated and permissions removed");
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error: any) {
      console.error("Error deactivating user:", error);
      toast.error(`Failed to deactivate user: ${error.message || "Unknown error"}`);
    }
  };

  const handlePermanentDelete = async (id: string, userName: string) => {
    try {
      // GDPR compliance: Permanent deletion
      // First remove from user_roles
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', id);
      
      if (roleError) {
        console.error("Error deleting roles:", roleError);
      }

      // Then delete the profile (cascades to related data)
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id);
          
      if (profileError) {
        throw profileError;
      }
      
      toast.success(`User ${userName} permanently deleted`);
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error: any) {
      console.error("Error permanently deleting user:", error);
      toast.error(`Failed to permanently delete user: ${error.message || "Unknown error"}`);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: 'active' | 'inactive') => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      const { error } = await supabase
        .from('profiles')
        .update({ status: newStatus })
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error: any) {
      console.error("Error toggling user status:", error);
      toast.error(`Failed to update user status: ${error.message || "Unknown error"}`);
    }
  };
  
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleResetPassword = async () => {
    try {
      if (!currentUser) return;
      if (!newPassword.trim() || newPassword.length < 6) {
        toast.error("Password must be at least 6 characters long");
        return;
      }
      
      setPasswordLoading(true);
      console.log("Resetting password for user:", currentUser.id);
      
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      
      if (!accessToken) {
        throw new Error("No access token available - please login again");
      }
      
      if (!SUPABASE_URL) {
        throw new Error("Supabase URL is not defined - please check your configuration");
      }
      
      const functionUrl = `${SUPABASE_URL}/functions/v1/admin-reset-password`;
      console.log("Function URL:", functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-client-info': 'users-page-reset-password'
        },
        body: JSON.stringify({
          userId: currentUser.id,
          password: newPassword,
          requestingUserId: user?.id
        })
      });
      
      console.log("Response status:", response.status);
      const responseData = await response.text();
      console.log("Response data:", responseData);
      
      let data;
      try {
        data = JSON.parse(responseData);
      } catch (e) {
        console.error("Failed to parse response as JSON:", e, "Raw response:", responseData);
        throw new Error(`Invalid response format from server: ${responseData}`);
      }
      
      if (!response.ok) {
        throw new Error(data.error || "Error from edge function");
      }
      
      toast.success("Password has been reset successfully");
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast.error(`Failed to reset password: ${error.message || "Unknown error"}`);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <AuthGuard requireAdmin>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Users</h1>
            <p className="text-muted-foreground">
              Manage user accounts and permissions
            </p>
          </div>

          <Dialog open={newUserDialogOpen} onOpenChange={setNewUserDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
                <DialogDescription>
                  Create a new user account and set their role
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={newUserData.name}
                    onChange={(e) =>
                      setNewUserData({ ...newUserData, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john.doe@example.com"
                    value={newUserData.email}
                    onChange={(e) =>
                      setNewUserData({ ...newUserData, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={newUserData.password}
                    onChange={(e) =>
                      setNewUserData({ ...newUserData, password: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={newUserData.role}
                    onValueChange={(value) =>
                      setNewUserData({ ...newUserData, role: value })
                    }
                  >
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Admin: Full access to all features
                    <br />
                    Editor: Can create and edit content
                    <br />
                    User: Limited access to content
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setNewUserDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateUser}>Create User</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>User Accounts</CardTitle>
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search users..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
                <span>Loading users...</span>
              </div>
            ) : (
              <div className="rounded-md border">
                <div className="grid grid-cols-1">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className="flex flex-wrap md:flex-nowrap items-center justify-between p-4 border-b last:border-b-0"
                      >
                        <div className="flex items-center space-x-4 w-full md:w-auto mb-4 md:mb-0">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={user.avatar} alt={user.name} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {user.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {user.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap md:flex-nowrap items-center gap-2 w-full md:w-auto">
                          <Badge
                            variant={
                              user.role === 'admin' ? 'default' : 'secondary'
                            }
                            className="flex items-center gap-1"
                          >
                            {user.role === 'admin' && (
                              <Shield className="h-3 w-3" />
                            )}
                            <span className="capitalize">{user.role}</span>
                          </Badge>
                          <Badge
                            variant={
                              user.status === 'active'
                                ? 'outline'
                                : 'destructive'
                            }
                            className="capitalize"
                          >
                            {user.status}
                          </Badge>
                          <div className="text-xs text-muted-foreground ml-2 hidden sm:block">
                            Last active: {formatDate(user.lastActive)}
                          </div>
                          <div className="flex items-center ml-auto gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setCurrentUser(user);
                                setEditUserDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Edit</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleStatus(user.id, user.status)}
                            >
                              {user.status === 'active' ? (
                                <X className="h-4 w-4" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                              <span className="sr-only">
                                {user.status === 'active'
                                  ? 'Deactivate'
                                  : 'Activate'}
                              </span>
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                  <span className="sr-only">Delete</span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Delete User Account
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="space-y-3">
                                    <p>
                                      Choose how to delete <strong>{user.name}</strong>:
                                    </p>
                                    <div className="space-y-2 text-sm">
                                      <p>
                                        <strong>Deactivate:</strong> Preserves user data for audit trails. 
                                        Immediately removes all permissions. Can be reactivated later.
                                      </p>
                                      <p>
                                        <strong>Permanent Delete:</strong> Completely removes user from database. 
                                        Cannot be undone. Use for GDPR "right to be forgotten" requests.
                                      </p>
                                    </div>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(user.id)}
                                    className="bg-orange-600 hover:bg-orange-700"
                                  >
                                    Deactivate (Recommended)
                                  </AlertDialogAction>
                                  <AlertDialogAction
                                    onClick={() => handlePermanentDelete(user.id, user.name)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Permanent Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-muted-foreground">
                        No users found. Try adjusting your search.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {currentUser && (
          <>
            <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit User</DialogTitle>
                  <DialogDescription>
                    Update user details and permissions
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Full Name</Label>
                    <Input
                      id="edit-name"
                      value={currentUser.name}
                      onChange={(e) =>
                        setCurrentUser({ ...currentUser, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={currentUser.email}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Email addresses cannot be changed
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-role">Role</Label>
                    <Select
                      value={currentUser.role}
                      onValueChange={(value) =>
                        setCurrentUser({
                          ...currentUser,
                          role: value as 'admin' | 'editor' | 'user',
                        })
                      }
                    >
                      <SelectTrigger id="edit-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select
                      value={currentUser.status}
                      onValueChange={(value) =>
                        setCurrentUser({
                          ...currentUser,
                          status: value as 'active' | 'inactive',
                        })
                      }
                    >
                      <SelectTrigger id="edit-status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditUserDialogOpen(false)}
                    disabled={updateLoading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setEditUserDialogOpen(false);
                      setPasswordDialogOpen(true);
                    }}
                    className="sm:mr-auto"
                  >
                    <Key className="h-4 w-4 mr-2" /> 
                    Reset Password
                  </Button>
                  <Button onClick={handleEditUser} disabled={updateLoading}>
                    {updateLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset User Password</DialogTitle>
                  <DialogDescription>
                    Set a new password for {currentUser.name}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <p className="text-xs text-muted-foreground">
                      Password must be at least 6 characters long
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPasswordDialogOpen(false);
                      setNewPassword('');
                      setEditUserDialogOpen(true);
                    }}
                    disabled={passwordLoading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResetPassword} 
                    disabled={passwordLoading || !newPassword.trim() || newPassword.length < 6}
                  >
                    {passwordLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Reset Password
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </AuthGuard>
  );
};

export default UsersPage;

